/**
 * /payroll-l2/retro-pay — Retroactive pay engine
 *
 * Allows calculating and posting retroactive pay adjustments.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { MoneyField } from "@/components/MoneyField";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { api } from "@/lib/api";
import type { Employee } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/retro-pay")({
  component: RetroPayPage,
});

function fmtMoney(v: string | number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

interface RetroPreviewRow {
  work_date: string;
  hours: string;
  old_amount: string;
  new_amount: string;
  delta: string;
}

interface RetroPostResult {
  pay_run_id: string;
  pay_run_number: string;
}

function RetroPayPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [form, setForm] = useState({
    employee_id: "",
    old_rate: "",
    new_rate: "",
    effective_date: "",
    period_start: "",
    period_end: "",
  });

  const [preview, setPreview] = useState<RetroPreviewRow[] | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [postedResult, setPostedResult] = useState<RetroPostResult | null>(null);

  const { data: employees = [] } = useQuery({
    queryKey: ["payroll-l2", "employees-list"],
    queryFn: async () => {
      const res = await api(`/api/payroll/employees?active_only=true&limit=500`);
      return (res as { items?: Employee[] }).items ?? (res as Employee[]);
    },
  });

  const previewMut = useMutation({
    mutationFn: async () => {
      const res = await api(`/api/payroll-l2/retro-pay/preview`, {
        method: "POST",
        body: {
          employee_id: form.employee_id,
          old_rate: form.old_rate,
          new_rate: form.new_rate,
          effective_date: form.effective_date,
          period_start: form.period_start,
          period_end: form.period_end,
        },
      });
      return res as RetroPreviewRow[];
    },
    onSuccess: (data) => {
      setPreview(data);
    },
  });

  const postMut = useMutation({
    mutationFn: async () => {
      const res = await api(`/api/payroll-l2/retro-pay/post`, {
        method: "POST",
        body: {
          employee_id: form.employee_id,
          old_rate: form.old_rate,
          new_rate: form.new_rate,
          effective_date: form.effective_date,
          period_start: form.period_start,
          period_end: form.period_end,
          preview_rows: preview,
        },
      });
      return res as RetroPostResult;
    },
    onSuccess: (data) => {
      setPostedResult(data);
      setConfirmOpen(false);
      qc.invalidateQueries({ queryKey: ["payroll-l2"] });
    },
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const retroTotal = preview?.reduce((sum, r) => sum + parseFloat(r.delta), 0) ?? 0;
  const formValid = form.employee_id && form.old_rate && form.new_rate && form.effective_date && form.period_start && form.period_end;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="h5" fontWeight={600}>Retro Pay</Typography>
        <HelpHint docPath="payroll-l2/retro-pay.md" label="Retro pay help" />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Recalculate and post retroactive pay when a rate change is applied to a prior period.
        Withholding uses the regular tax engine (FIT, FICA, FUTA, state). Negative
        retros (clawbacks) are not supported here.{" "}
        Help: <code>docs/help/payroll-l2/retro-pay.md</code> · or open chat (⌘J).
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Configuration</Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            select
            label="Employee"
            value={form.employee_id}
            onChange={set("employee_id")}
            required
          >
            {(employees as Employee[]).map((e) => (
              <MenuItem key={e.id} value={e.id}>
                {e.name} ({e.code})
              </MenuItem>
            ))}
          </TextField>

          <Box /> {/* spacer */}

          <MoneyField
            label="Old rate / hr"
            value={form.old_rate}
            onChange={set("old_rate")}
            required
          />
          <MoneyField
            label="New rate / hr"
            value={form.new_rate}
            onChange={set("new_rate")}
            required
          />
          <TextField
            label="Effective Date"
            type="date"
            value={form.effective_date}
            onChange={set("effective_date")}
            InputLabelProps={{ shrink: true }}
            required
          />
          <Box /> {/* spacer */}

          <TextField
            label="Pay Period Start"
            type="date"
            value={form.period_start}
            onChange={set("period_start")}
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="Pay Period End"
            type="date"
            value={form.period_end}
            onChange={set("period_end")}
            InputLabelProps={{ shrink: true }}
            required
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="warning"
            startIcon={previewMut.isPending ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            onClick={() => previewMut.mutate()}
            disabled={!formValid || previewMut.isPending}
          >
            {previewMut.isPending ? "Calculating…" : "Calculate Retro Pay"}
          </Button>
        </Box>

        {previewMut.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>{String((previewMut.error as Error)?.message)}</Alert>
        )}
      </Paper>

      {/* Preview table */}
      {preview && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Retro Pay Preview</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Hours</TableCell>
                <TableCell align="right">Old Amount</TableCell>
                <TableCell align="right">New Amount</TableCell>
                <TableCell align="right">Delta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.work_date}</TableCell>
                  <TableCell align="right">{row.hours}</TableCell>
                  <TableCell align="right">{fmtMoney(row.old_amount)}</TableCell>
                  <TableCell align="right">{fmtMoney(row.new_amount)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: parseFloat(row.delta) >= 0 ? "success.main" : "error.main",
                      fontWeight: 600,
                    }}
                  >
                    {parseFloat(row.delta) >= 0 ? "+" : ""}{fmtMoney(row.delta)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ "& td": { fontWeight: 700, borderTop: "2px solid", borderColor: "divider" } }}>
                <TableCell colSpan={4} align="right">Total Retro Pay:</TableCell>
                <TableCell
                  align="right"
                  sx={{ color: retroTotal >= 0 ? "success.main" : "error.main" }}
                >
                  {retroTotal >= 0 ? "+" : ""}{fmtMoney(retroTotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Divider sx={{ my: 2 }} />

          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            Post Retro Pay Run
          </Button>
        </Paper>
      )}

      {/* Success */}
      {postedResult && (
        <Alert
          severity="success"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate({ to: "/payroll/pay-runs/$payRunId", params: { payRunId: postedResult.pay_run_id } })}
            >
              View Pay Run
            </Button>
          }
        >
          Retro pay run {postedResult.pay_run_number} created successfully.
        </Alert>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Post Retro Pay Run</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Typography variant="body2" mb={1}>
            This will create a new pay run for the retroactive pay adjustment.
          </Typography>
          <Typography variant="h6" fontWeight={600} color={retroTotal >= 0 ? "success.main" : "error.main"}>
            Total: {retroTotal >= 0 ? "+" : ""}{fmtMoney(retroTotal)}
          </Typography>
          {postMut.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{String((postMut.error as Error)?.message)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => postMut.mutate()}
            disabled={postMut.isPending}
          >
            {postMut.isPending ? "Posting…" : "Confirm & Post"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
